
import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { ArrowLeft, Download, RotateCcw, CheckCircle, Printer } from "lucide-react";
import { Worksheet } from "@/types/worksheet";
import PuzzleWorksheet from "@/components/worksheets/types/PuzzleWorksheet";
import MatchingWorksheet from "@/components/worksheets/types/MatchingWorksheet";
import SortingWorksheet from "@/components/worksheets/types/SortingWorksheet";
import PrintableWorksheet from "@/components/worksheets/PrintableWorksheet";

interface WorksheetViewerProps {
  worksheet: Worksheet;
  onBack: () => void;
}

const WorksheetViewer = ({ worksheet, onBack }: WorksheetViewerProps) => {
  const [progress, setProgress] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [score, setScore] = useState(0);
  const [key, setKey] = useState(0);
  const [showPrintView, setShowPrintView] = useState(false);

  const handleProgressUpdate = (newProgress: number, newScore?: number) => {
    setProgress(newProgress);
    if (newScore !== undefined) {
      setScore(newScore);
    }
    if (newProgress >= 100) {
      setIsCompleted(true);
    }
  };

  const handleReset = () => {
    setProgress(0);
    setScore(0);
    setIsCompleted(false);
    setKey(prev => prev + 1);
  };

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
      setShowPrintView(false);
    }, 100);
  };

  const handleDownloadPDF = async () => {
    try {
      handlePrint();
    } catch (error) {
      console.error('Failed to generate PDF:', error);
    }
  };

  const renderWorksheetContent = () => {
    switch (worksheet.type.category) {
      case 'puzzle':
        return (
          <PuzzleWorksheet
            key={key}
            content={worksheet.content}
            onProgressUpdate={handleProgressUpdate}
            showPrintView={showPrintView}
          />
        );
      case 'matching':
        return (
          <MatchingWorksheet
            key={key}
            content={worksheet.content}
            onProgressUpdate={handleProgressUpdate}
            showPrintView={showPrintView}
          />
        );
      case 'sorting':
        return (
          <SortingWorksheet
            key={key}
            content={worksheet.content}
            onProgressUpdate={handleProgressUpdate}
            showPrintView={showPrintView}
          />
        );
      default:
        return <div>Worksheet type not supported yet.</div>;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'hard': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getProgressColor = () => {
    if (progress < 30) return 'bg-red-500';
    if (progress < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  if (showPrintView) {
    return (
      <div className="print:block">
        <style>
          {`
            @media print {
              body * { visibility: hidden; }
              .print-content, .print-content * { visibility: visible; }
              .print-content { position: absolute; left: 0; top: 0; width: 100%; }
              .no-print { display: none !important; }
            }
            @media screen {
              .print-content { display: none; }
            }
          `}
        </style>
        <div className="print-content">
          {renderWorksheetContent()}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 py-8">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center justify-between mb-6 no-print">
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-blue-600 hover:text-blue-800 hover:bg-blue-100"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Activities
            </Button>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={handleReset}
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Reset
              </Button>
              
              <Button
                variant="outline"
                onClick={handlePrint}
                className="text-purple-600 border-purple-200 hover:bg-purple-50"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
              
              <Button
                variant="outline"
                onClick={handleDownloadPDF}
                className="text-green-600 border-green-200 hover:bg-green-50"
              >
                <Download className="h-4 w-4 mr-2" />
                PDF
              </Button>
            </div>
          </div>

          <Card className="mb-6 border-2 border-blue-200 shadow-lg no-print">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-2xl font-bold text-blue-700 mb-2">
                    {worksheet.title}
                  </CardTitle>
                  <p className="text-gray-600">{worksheet.type.description}</p>
                </div>
                <div className="flex gap-2">
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    <span className="text-sm">Grade {worksheet.grade}</span>
                  </Badge>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    {worksheet.subject}
                  </Badge>
                  <Badge variant="secondary" className={getDifficultyColor(worksheet.difficulty)}>
                    {worksheet.difficulty.charAt(0).toUpperCase() + worksheet.difficulty.slice(1)}
                  </Badge>
                </div>
              </div>
              
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Progress {isCompleted && '- Completed! 🎉'}
                  </span>
                  <span className="text-sm text-gray-600">{Math.round(progress)}%</span>
                </div>
                <Progress 
                  value={progress} 
                  className="h-3"
                  indicatorClassName={getProgressColor()}
                />
                {isCompleted && (
                  <motion.div 
                    className="flex items-center mt-2 text-green-600"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <CheckCircle className="h-5 w-5 mr-2" />
                    <span className="font-medium">
                      Excellent work! Final Score: {score}%
                    </span>
                  </motion.div>
                )}
              </div>
            </CardHeader>
          </Card>

          <div className="worksheet-content">
            {renderWorksheetContent()}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default WorksheetViewer;
